import type { CSSProperties, ReactNode } from 'react';
import cn from 'classnames';
import IconAt from '@/assets/icons/icon-at.svg';
import { AudioIcon } from '@/ui-components/atoms/AnimatedIcons/AudioIcon/AudioIcon';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';
import { Text } from '@/ui-components/atoms/Text/Text';
import styles from './sportsScheduleStrip.module.scss';

export interface SportsScheduleEntity {
    name: string;
    image?: ReactNode;
    href?: string;
    description?: string;
    channels?: ReactNode;
    listenAction?: ReactNode;
    color?: string;
}

interface StripProps {
    startTime: string;
    isLive?: boolean;
}

export type SportsScheduleStripProps = StripProps &
    (
        | {
              variant: 'matchup';
              awayTeam: SportsScheduleEntity;
              homeTeam: SportsScheduleEntity;
              separator?: 'at' | 'versus';
          }
        | { variant: 'event'; event: SportsScheduleEntity; driverChannels?: ReactNode }
    );

function Entity({ entity, isLive }: { entity: SportsScheduleEntity; isLive?: boolean }) {
    return (
        <div className={styles.entity} style={{ '--team-color': entity.color } as CSSProperties}>
            <div className={styles.entityContent}>
                {entity.image && <div className={styles.art}>{entity.image}</div>}
                <div className={styles.metadata}>
                    <div className={styles.titles} title={entity.name}>
                        <LinkWrapperIfHref link={{ href: entity.href }} className={styles.nameLink}>
                            <Text tag="p" className={styles.name}>
                                {entity.name}
                            </Text>
                        </LinkWrapperIfHref>
                        {entity.description && (
                            <Text tag="p" className={styles.description}>
                                {entity.description}
                            </Text>
                        )}
                    </div>
                    {entity.channels && <div className={styles.channels}>{entity.channels}</div>}
                </div>
            </div>
            <div className={styles.listenAction}>{isLive && entity.listenAction}</div>
        </div>
    );
}

const SEPARATORS = {
    at: (
        <span className={styles.matchup} role="img" aria-label="at">
            <IconAt aria-hidden="true" focusable="false" />
        </span>
    ),
    versus: <span className={styles.matchup}>vs</span>,
};

function StripEntities(props: SportsScheduleStripProps) {
    if (props.variant === 'event') return <Entity entity={props.event} isLive={props.isLive} />;

    if (props.separator === 'versus') {
        return (
            <>
                <Entity entity={props.homeTeam} isLive={props.isLive} />
                {SEPARATORS.versus}
                <Entity entity={props.awayTeam} isLive={props.isLive} />
            </>
        );
    }

    return (
        <>
            <Entity entity={props.awayTeam} isLive={props.isLive} />
            {SEPARATORS.at}
            <Entity entity={props.homeTeam} isLive={props.isLive} />
        </>
    );
}

export function SportsScheduleStrip(props: SportsScheduleStripProps) {
    return (
        <div className={cn(styles.strip, { [styles.event]: props.variant === 'event', [styles.live]: props.isLive })}>
            <div className={styles.row}>
                <div className={styles.time}>
                    <Text tag="span" className={styles.startTime}>
                        {props.startTime}
                    </Text>
                    {props.isLive && (
                        <span role="img" aria-label="Live" className={styles.liveIndicator}>
                            <AudioIcon />
                        </span>
                    )}
                </div>
                <div className={styles.entities}>
                    <StripEntities {...props} />
                </div>
            </div>
            {props.variant === 'event' && props.driverChannels}
        </div>
    );
}
